import { logger } from '@adornis/base/logging.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { NarrowedEntityData } from '@adornis/bh-util/selectionset-types.js';
import { loadRawEntityDataByIDs, loadRawEntityDataByQuery } from '@adornis/bh-util/util.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { differenceInDays, format } from 'date-fns';
import type { TemplateResult } from 'lit';
import { getInstitutionByID } from '../api/institution.js';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { Institution } from '../entities/institution.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { Participation } from '../entities/Participation.js';
import { SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import { Workshop } from '../entities/workshop.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { buildNewsletterOptinLink, buildRegistrationsHTML } from '../mails/mail-helpers.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { getNotificationType, sendWhatsappWithEmailFallback } from './_helper.js';

export async function notifyNachrueckplatzErhalten(
  institutionID: string,
  user: NarrowedEntityData<
    User,
    {
      _id: 1;
      _class: 1;
      email: 1;
      title: 1;
      firstName: 1;
      lastName: 1;
      whatsappNotification: 1;
      whatsappPhone: 1;
    }
  >,
  participations: NarrowedEntityData<
    Participation,
    {
      _id: 1;
      productID: 1;
      state: 1;
      waitingListPlace: 1;
    }
  >[],
) {
  const institution = await getInstitutionByID(institutionID)({
    ...Institution.allFields,
    colorDefinitions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });
  if (!institution) throw new Error('could not find institution');

  const workshops = await loadRawEntityDataByIDs(
    Workshop,
    participations.map(p => p.productID as string),
    {
      _class: 1,
      _id: 1,
      name: 1,
      description: 1,
      translatedDescriptionHTML: { _class: 1, langKey: 1, text: 1 },
      sortingWeight: 1,
      hasPrice: 1,
      categoryID: 1,
      institutionID: 1,
      timeframeID: 1,
      attachedCertificate: { _class: 1, certificateID: 1, exportDate: 1 },
      publishDate: 1,
      topic: 1,
      language: 1,
      presenceType: 1,
      isCanceled: 1,
      appointments: {
        _class: 1,
        _id: 1,
        start: 1,
        end: 1,
        isNonBlocking: 1,
        location: 1,
        name: 1,
      },
      capacity: 1,
      hideCapacity: 1,
    },
  );

  // set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  const mailTemplate = (await getMailTemplateForInstitution(
    institutionID,
    MailTemplateType.NACHRUECKPLATZ_ERHALTEN,
  )(InstitutionMailTemplate.allFields)) as InstitutionMailTemplate;

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  const otherParticipations = (await loadRawEntityDataByQuery(Participation, { userID: user._id })).filter(
    p => !participations.map(_p => p._id).includes(p._id),
  );

  const otherProducts = await loadRawEntityDataByIDs(
    Workshop,
    otherParticipations.map(p => p.productID as string),
    {
      _class: 1,
      _id: 1,
      name: 1,
      topic: 1,
      language: 1,
      appointments: {
        _class: 1,
        _id: 1,
        start: 1,
        end: 1,
        isNonBlocking: 1,
        location: 1,
        name: 1,
      },
    },
  );

  const start = workshops[0]?.appointments?.[0]?.start;
  const daysTillEvent = start ? differenceInDays(start, new Date()) : '';
  const dates = workshops[0]?.appointments?.map(a => (a.start ? format(a.start, 'dd.MM.yyyy') : 'tbd')).join(', ');

  const mailVars = {
    [MailTemplateVars.EVENT_TITLE]: workshops.map(w => w.name).join(', ') ?? '',
    [MailTemplateVars.PRODUCT_NAME]: workshops.map(w => w.name).join(', ') ?? '',
    [MailTemplateVars.EVENT_DATES]: dates,
    [MailTemplateVars.EVENT_LEAD_DAYS]: daysTillEvent,
    [MailTemplateVars.TITLE]: user.title ?? '',
    [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(user),
    [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(user),
    [MailTemplateVars.CHANGED_REGISTRATIONS]: buildRegistrationsHTML(
      participations.map(participation => {
        const workshop = workshops.find(w => w._id === participation.productID);
        if (!workshop) throw new Error('workshop not found: ' + participation.productID);
        return {
          name: workshop.name ?? '',
          state: participation.state,
          topic: workshop.topic,
          language: workshop.language,
          waitingListPlace: undefined,
          institution,
          appointments: workshop.appointments,
          englishVersion: false,
        };
      }),
      true,
    ),
    [MailTemplateVars.CHANGED_REGISTRATIONS_EN]: buildRegistrationsHTML(
      participations.map(participation => {
        const workshop = workshops.find(w => w._id === participation.productID);
        if (!workshop) throw new Error('workshop not found: ' + participation.productID);
        return {
          name: workshop.name ?? '',
          state: participation.state,
          topic: workshop.topic,
          language: workshop.language,
          waitingListPlace: undefined,
          institution,
          appointments: workshop.appointments,
          englishVersion: true,
        };
      }),
      true,
    ),
    [MailTemplateVars.OTHER_REGISTRATIONS]: buildRegistrationsHTML(
      otherParticipations.map(participation => {
        const workshop = otherProducts.find(w => w._id === participation.productID);
        if (!workshop) throw new Error('workshop not found: ' + participation.productID);
        return {
          name: workshop.name ?? '',
          state: participation.state,
          topic: workshop.topic,
          language: workshop.language,
          waitingListPlace: undefined,
          institution,
          appointments: workshop.appointments,
          englishVersion: true,
        };
      }),
      true,
    ),
    [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
    [MailTemplateVars.NEWSLETTER_OPTIN_LINK]: buildNewsletterOptinLink(token, 'de'),
    [MailTemplateVars.NEWSLETTER_OPTIN_LINK_EN]: buildNewsletterOptinLink(token, 'en'),
  };

  const { content: mailContent } = await assembleInstitutionMail({ template: mailTemplate, infos: mailVars as any });
  if (!mailContent) {
    logger.warn({ institutionID }, 'could not generate mailTemplate');
    return;
  }

  const sendEmail = async () => {
    const { subject, content } = await assembleInstitutionMail({ template: mailTemplate, infos: mailVars as any });

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    await sendInstitutionEmail({
      email: user.email,
      subject,
      content: content as TemplateResult<1>,
      institutionID,
    });
  };

  const notificationType = await getNotificationType(user, mailTemplate, institutionID);
  if (notificationType === 'whatsapp') {
    await sendWhatsappWithEmailFallback({
      institutionID,
      user: { _id: user._id, whatsappPhone: user.whatsappPhone!, email: user.email },
      mailTemplate,
      mailTemplateType: MailTemplateType.NACHRUECKPLATZ_ERHALTEN,
      parameters: [
        InstitutionMailTemplate.mapFirstNameVar(user) || '-',
        InstitutionMailTemplate.mapLastNameVar(user) || '-',
        workshops.map(w => w.name).join(', ') || '-',
        InstitutionMailTemplate.workshopTerminString({
          appointments: workshops[0]?.appointments ?? [],
        }) || '-',
      ],
      sendEmail,
    });
  } else if (notificationType === 'email') {
    await sendEmail();
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }
}

defineMailTemplate(MailTemplateType.NACHRUECKPLATZ_ERHALTEN, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Nachrückplatz erhalten',
    content: `<p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>gute Neuigkeiten, soeben ist ein Platz in unserer Veranstaltung&nbsp; frei geworden. Da Sie auf dem n&auml;chsten Platz der Warteliste standen, haben wir Sie verbindlich in die Teilnehmer*innenliste eingetragen.</p>
<p>Nachstehend f&uuml;hren wir nochmals die wesentlichen Informationen der Veranstaltung auf:</p>
<p>{{changed-registrations}}</p>
<p>Eine Auflistung all Ihrer Workshops und die M&ouml;glichkeit, sich wieder abmelden zu k&ouml;nnen, finden Sie in Ihrer Anmeldeverwaltung: {{login-link}}</p>
<p><strong>Sie k&ouml;nnen nicht teilnehmen? Bitte abmelden und dadurch anderen Studierenden die Teilnahme erm&ouml;glichen</strong></p>
<p>Falls die den Termin nicht wahrnehmen k&ouml;nnen, bitten wir Sie, sich zeitnah in ihrer Anmeldeverwaltung von der Veranstaltung abzumelden. Studierende auf der Warteliste profitieren dann von Ihrer Abmeldung.</p>
<p>Eine Abmeldung ist nur bis sp&auml;testens 6 Tage vor Workshop-Beginn m&ouml;glich. Nach Ablauf dieser Frist ist eine Abmeldung nur <a href="mailto:contact@anmeldung.uni-frankfurt.de">per Mail </a> mit Angabe eines triftigen Grundes m&ouml;glich. Bei Nichterscheinen ohne vorherige Absage behalten wir uns vor, Sie von weiteren Workshops auszuschlie&szlig;en bzw. Ihnen entstehende Kosten in Rechnung zu stellen.</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>---------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>Good news, a spot has just become&nbsp; available in our event. As you were on the next place on the waiting list, we have bindingly entered you in the list of participants.</p>
<p>Below we list again the essential information of the event:</p>
<p>{{changed-registrations}}</p>
<p>You will find a list of all your workshops and the possibility to cancel your registration in your registration administration:&nbsp; {{login-link}}</p>
<p><strong>You are unable to attend? Please deregister and thereby enable other students to participate.</strong> If you are unable to attend the event, please deregister promptly in your registration administration. Students on the waiting list will then benefit from your deregistration.</p>
<p>Deregistration is only possible up to 6 days before the start of the workshop. After this deadline, cancellation is only possible by e-mail with a valid reason. In the case of non-attendance without prior cancellation, we reserve the right to exclude you from further workshops or to charge you for any costs incurred.</p>
<p>Best regards</p>`,
    footer: '[footer]',
  },
  variables: institutionMailVariables(MailTemplateType.NACHRUECKPLATZ_ERHALTEN),
  attachments: [],
  displayName: 'Nachrückplatz erhalten',
  editorHint: () => institutionMailHint(MailTemplateType.NACHRUECKPLATZ_ERHALTEN),
});
